import { useCallback, useEffect, useMemo, useState } from 'react'

import { api, ErrorApi, type FilaCatalogo } from '../api'
import { FilaItemCatalogo } from './FilaItemCatalogo'
import { NuevoItemCatalogo } from './NuevoItemCatalogo'

type Origen = 'todos' | 'propio' | 'motor'
type Estado = 'activos' | 'inactivos' | 'todos'

const ORIGENES: { valor: Origen; texto: string }[] = [
  { valor: 'todos', texto: 'Todos' },
  { valor: 'propio', texto: 'Nuevos' },
  { valor: 'motor', texto: 'Del motor' },
]
const ESTADOS: { valor: Estado; texto: string }[] = [
  { valor: 'activos', texto: 'Activos' },
  { valor: 'inactivos', texto: 'Inactivos' },
  { valor: 'todos', texto: 'Todos' },
]

/**
 * Lista única de precios: los items nuevos de SunSmart y los productos que ya trae el motor.
 * Solo el admin crea, cambia precios y desactiva; los asesores la consultan. No hay «Borrar».
 */
export function Catalogo({ esAdmin }: { esAdmin: boolean }) {
  const [filas, setFilas] = useState<FilaCatalogo[]>([])
  const [motorDisponible, setMotorDisponible] = useState(true)
  const [origen, setOrigen] = useState<Origen>('todos')
  const [estado, setEstado] = useState<Estado>('activos')
  const [buscar, setBuscar] = useState('')
  const [error, setError] = useState('')
  const [cargando, setCargando] = useState(true)

  const cargar = useCallback(async () => {
    try {
      const r = await api.catalogo()
      setFilas(r.filas)
      setMotorDisponible(r.motor_disponible)
      setError('')
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo cargar.')
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => {
    void cargar()
  }, [cargar])

  const visibles = useMemo(() => {
    const q = buscar.trim().toLowerCase()
    return filas.filter(
      (f) =>
        (origen === 'todos' || f.origen === origen) &&
        (estado === 'todos' || f.activo === (estado === 'activos')) &&
        (!q || f.nombre.toLowerCase().includes(q) || f.codigo.toLowerCase().includes(q) || f.proveedor.toLowerCase().includes(q)),
    )
  }, [filas, origen, estado, buscar])

  return (
    <section>
      <div className="encabezado">
        <div>
          <h1>Catálogo</h1>
          <p className="suave">
            {esAdmin
              ? 'Agrega paneles, inversores y baterías con su dato técnico, su costo y su IVA: salen en las listas de equipos de la cotización. Cambia precios y desactiva lo que ya no se vende. Nada se borra.'
              : 'Los precios vigentes. Solo el administrador los cambia.'}
          </p>
        </div>
      </div>

      {esAdmin && <NuevoItemCatalogo alCrear={cargar} />}

      <div className="filtros">
        {ORIGENES.map((o) => (
          <button key={o.valor} className={origen === o.valor ? 'chip activo' : 'chip'} onClick={() => setOrigen(o.valor)}>
            {o.texto}
          </button>
        ))}
        <span className="separador" />
        {ESTADOS.map((e) => (
          <button key={e.valor} className={estado === e.valor ? 'chip activo' : 'chip'} onClick={() => setEstado(e.valor)}>
            {e.texto}
          </button>
        ))}
        <input className="buscador-catalogo" type="search" value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Buscar por nombre, código o proveedor" aria-label="Buscar en el catálogo" />
      </div>

      {!motorDisponible && <p className="error">El motor no responde: solo se ven los items nuevos. Los productos del motor vuelven cuando arranque.</p>}
      {error && <p className="error">{error}</p>}
      {!cargando && visibles.length === 0 && !error && <p className="vacio">No hay items con ese filtro.</p>}

      {visibles.length > 0 && (
        <div className="tabla-envoltura">
          <table className="tabla compacta">
            <thead>
              <tr>
                <th>Item</th>
                <th>Tipo</th>
                <th>Origen</th>
                <th className="num">Costo (USD)</th>
                <th>IVA</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visibles.map((f) => (
                <FilaItemCatalogo key={f.codigo} fila={f} puedeEditar={esAdmin} alCambiar={cargar} alFallar={setError} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="suave">El costo es para SunSmart, sin IVA ni utilidad: cada cotización le suma la suya. Desactivar un producto del motor solo lo quita de los equipos a mano.</p>
    </section>
  )
}
